import { Point, Vector } from 'content-creation-common/models';
import { ELEMENT_HANDLES, TEXT_ALIGNMENT } from '../constants';
import {
  convertPixelToEm,
  getNumericFontValue,
  wrapFontFamilyInQuotes,
} from './font';
import { isNil } from './object';
import { isEmpty, isNotEmpty } from './array';

/**
 * @function isElementVisibleInContainer
 * @param  {DOMElement} element
 * @param  {DOMElement} containerElement
 * @return {boolean} returns true if the given element is fully visible(constrained) in the container element
 */
export const isElementVisibleInContainer = (element, containerElement) => {
  containerElement = containerElement || document.body;
  const { top, bottom, left, right } = element.getBoundingClientRect();
  const {
    top: containerTop,
    bottom: containerBottom,
    left: containerLeft,
    right: containerRight,
  } = containerElement.getBoundingClientRect();

  return (
    top >= containerTop &&
    bottom <= containerBottom &&
    left >= containerLeft &&
    right <= containerRight
  );
};

/*
 Check if this node is inside of any node with any class name passed in
 'ant-select-dropdown-menu-item'
 */
export function isInsideClass(node, classNames = []) {
  let parent = node;

  const finder = (className) => {
    return parent.classList.contains(className);
  };

  while (parent) {
    if (parent.classList && classNames.find(finder)) {
      return true;
    }
    parent = parent.parentNode;
  }
  return false;
}

export function isDescendantNode(parent, child) {
  if (isNil(parent) || isNil(child)) return false;
  const { parentNode: childParentNode } = child;

  if (childParentNode === parent) return true;
  return isDescendantNode(parent, childParentNode);
}

export const getTextAlign = (alignment, isRTL = false) => {
  if (isRTL) {
    switch (alignment) {
      case TEXT_ALIGNMENT.LEFT:
        return 'right';
      case TEXT_ALIGNMENT.CENTER:
        return 'center';
      case TEXT_ALIGNMENT.RIGHT:
        return 'left';
      default:
        return 'left';
    }
  }

  switch (alignment) {
    case TEXT_ALIGNMENT.LEFT:
      return 'left';
    case TEXT_ALIGNMENT.CENTER:
      return 'center';
    case TEXT_ALIGNMENT.RIGHT:
      return 'right';
    default:
      return 'left';
  }
};

export function measureTextWithDom(
  text = '...',
  fontFamily,
  fontSize,
  padding,
  alignment = TEXT_ALIGNMENT.LEFT,
  letterSpacing,
  lineSpacing,
) {
  const testNode = document.createElement('div');
  testNode.className = 'text-measure';

  testNode.style.fontFamily = wrapFontFamilyInQuotes(fontFamily);
  testNode.style.fontSize = `${getNumericFontValue(fontSize)}px`;
  testNode.style.letterSpacing = `${convertPixelToEm(letterSpacing)}em`;
  testNode.style.lineHeight = `${lineSpacing}em`;
  testNode.style.padding = `${padding}px`;
  testNode.style.textAlign = getTextAlign(alignment);

  // Add the text
  testNode.innerText = text;

  // Make room for the next line of text
  if (text.length === 0 || text[text.length - 1] === '\n') {
    testNode.innerText += 'a';
  }

  document.body.appendChild(testNode);
  const fullTextRect = testNode.getBoundingClientRect();
  const { width, height } = fullTextRect;
  document.body.removeChild(testNode);

  return {
    height: Math.ceil(height),
    width: Math.ceil(width),
  };
}

export function getCoordinates(
  center,
  height,
  width,
  scaleX,
  scaleY,
  rotation,
) {
  const cornerCoordinates = getCornerCoordinates(
    center,
    height,
    width,
    scaleX,
    scaleY,
    rotation,
  );
  const {
    topLeft: r1,
    topRight: r2,
    bottomLeft: r3,
    bottomRight: r4,
  } = cornerCoordinates;

  const maxX = Math.max(r1.x, r2.x, r3.x, r4.x);
  const minX = Math.min(r1.x, r2.x, r3.x, r4.x);
  const maxY = Math.max(r1.y, r2.y, r3.y, r4.y);
  const minY = Math.min(r1.y, r2.y, r3.y, r4.y);

  // parallel sides
  if (Math.abs(rotation % 90) === 0) {
    return {
      ...cornerCoordinates,
      center,
      top: new Point(maxX, minY),
      bottom: new Point(minX, maxY),
      left: new Point(minX, minY),
      right: new Point(maxX, maxY),
    };
  } else {
    // x => y
    const xToY = {
      [r1.x]: r1.y,
      [r2.x]: r2.y,
      [r3.x]: r3.y,
      [r4.x]: r4.y,
    };

    // y => x
    const yToX = {
      [r1.y]: r1.x,
      [r2.y]: r2.x,
      [r3.y]: r3.x,
      [r4.y]: r4.x,
    };

    return {
      ...cornerCoordinates,
      center,
      top: new Point(yToX[minY], minY),
      bottom: new Point(yToX[maxY], maxY),
      left: new Point(minX, xToY[minX]),
      right: new Point(maxX, xToY[maxX]),
    };
  }
}

export function getCornerCoordinates(
  center,
  height,
  width,
  scaleX,
  scaleY,
  rotation,
) {
  const { x, y } = center;
  const topLeft = new Point(
    x - (width * scaleX) / 2,
    y - (height * scaleY) / 2,
  );

  const bottomLeft = new Point(topLeft.x, topLeft.y + height * scaleY);

  const topRight = new Point(topLeft.x + width * scaleX, topLeft.y);

  const bottomRight = new Point(topRight.x, bottomLeft.y);

  topLeft.rotateAroundPoint(center, rotation);
  topRight.rotateAroundPoint(center, rotation);
  bottomLeft.rotateAroundPoint(center, rotation);
  bottomRight.rotateAroundPoint(center, rotation);

  return {
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
  };
}

export function getCoLinearYCoordinate(point1, point2, x) {
  if (point1.y === point2.y) return point1.y;

  const a = (point1.y - point2.y) / (point1.x - point2.x);
  const b = point1.y - a * point1.x;
  return a * x + b;
}

export function getCoLinearXCoordinate(point1, point2, y) {
  if (point1.x === point2.x) return point1.x;

  const a = (point1.y - point2.y) / (point1.x - point2.x);
  const b = point1.y - a * point1.x;
  return (y - b) / a;
}

// Math.sqrt((x2-x2)^2 + (y2-y1)^2)
export function getDistance(point1, point2) {
  return Math.sqrt(
    Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2),
  );
}

export function getScaleOffsets(
  corner,
  cornerCoordinates,
  scaledCornerCoordinates,
) {
  switch (corner) {
    case ELEMENT_HANDLES.BOTTOM_RIGHT:
      return {
        x: cornerCoordinates.topLeft.x - scaledCornerCoordinates.topLeft.x,
        y: cornerCoordinates.topLeft.y - scaledCornerCoordinates.topLeft.y,
      };
    case ELEMENT_HANDLES.BOTTOM_LEFT:
      return {
        x: cornerCoordinates.topRight.x - scaledCornerCoordinates.topRight.x,
        y: cornerCoordinates.topRight.y - scaledCornerCoordinates.topRight.y,
      };
    case ELEMENT_HANDLES.TOP_LEFT:
      return {
        x:
          cornerCoordinates.bottomRight.x -
          scaledCornerCoordinates.bottomRight.x,
        y:
          cornerCoordinates.bottomRight.y -
          scaledCornerCoordinates.bottomRight.y,
      };
    case ELEMENT_HANDLES.TOP_RIGHT:
      return {
        x:
          cornerCoordinates.bottomLeft.x - scaledCornerCoordinates.bottomLeft.x,
        y:
          cornerCoordinates.bottomLeft.y - scaledCornerCoordinates.bottomLeft.y,
      };
    default:
      return { x: 0, y: 0 };
  }
}

export function getSideScaleOffsets(
  side,
  cornerCoordinates,
  scaleX = 1,
  scaleY = 1,
) {
  let vector;
  let distance;
  let finalPoint;

  switch (side) {
    case ELEMENT_HANDLES.TOP:
      vector = new Vector(
        cornerCoordinates.bottomLeft,
        cornerCoordinates.topLeft,
      );
      // (scale * length) - length -> length * (scale - 1)
      distance = (vector.length * (scaleY - 1)) / 2;
      finalPoint = Vector.getPointFromEnd(vector, distance);

      return {
        x: finalPoint.x - cornerCoordinates.topLeft.x,
        y: finalPoint.y - cornerCoordinates.topLeft.y,
      };
    case ELEMENT_HANDLES.RIGHT:
      vector = new Vector(
        cornerCoordinates.topLeft,
        cornerCoordinates.topRight,
      );
      distance = (vector.length * (scaleX - 1)) / 2;
      finalPoint = Vector.getPointFromEnd(vector, distance);

      return {
        x: finalPoint.x - cornerCoordinates.topRight.x,
        y: finalPoint.y - cornerCoordinates.topRight.y,
      };
    case ELEMENT_HANDLES.BOTTOM:
      vector = new Vector(
        cornerCoordinates.topLeft,
        cornerCoordinates.bottomLeft,
      );
      distance = (vector.length * (scaleY - 1)) / 2;
      finalPoint = Vector.getPointFromEnd(vector, distance);

      return {
        x: finalPoint.x - cornerCoordinates.bottomLeft.x,
        y: finalPoint.y - cornerCoordinates.bottomLeft.y,
      };
    case ELEMENT_HANDLES.LEFT:
      vector = new Vector(
        cornerCoordinates.topRight,
        cornerCoordinates.topLeft,
      );
      distance = (vector.length * (scaleX - 1)) / 2;
      finalPoint = Vector.getPointFromEnd(vector, distance);

      return {
        x: finalPoint.x - cornerCoordinates.topLeft.x,
        y: finalPoint.y - cornerCoordinates.topLeft.y,
      };
    default:
      return { x: 0, y: 0 };
  }
}

export function isOverlapping(shape1, shape2) {
  const { top: top1, bottom: bottom1, left: left1, right: right1 } = shape1;
  const { top: top2, bottom: bottom2, left: left2, right: right2 } = shape2;

  if (
    !top1 ||
    !bottom1 ||
    !left1 ||
    !right1 ||
    !top2 ||
    !bottom2 ||
    !left2 ||
    !right2 ||
    isNaN(top1.y) ||
    isNaN(bottom1.y) ||
    isNaN(left1.x) ||
    isNaN(right1.x) ||
    isNaN(top2.y) ||
    isNaN(bottom2.y) ||
    isNaN(left2.x) ||
    isNaN(right2.x)
  ) {
    return false;
  }

  // check if each corner points and its center of a rectangle is inside another shape
  const shape1Bounds = [top1, right1, bottom1, left1];
  const shape2Bounds = [top2, right2, bottom2, left2];
  return polygonOverlaps(shape1Bounds, shape2Bounds);
}

// checks to see if one polygon overlaps another
// slightly modified from the following resource:
// https://stackoverflow.com/a/12414951
export function polygonOverlaps(polygonA, polygonB) {
  const polygons = [polygonA, polygonB];
  let minA;
  let maxA;
  let minB;
  let maxB;
  let projected;
  let i;
  let i1;
  let j;

  for (i = 0; i < polygons.length; i++) {
    // for each polygon, look at each edge of the polygon, and determine if it separates the two shapes
    const polygon = polygons[i];
    for (i1 = 0; i1 < polygon.length; i1++) {
      // grab 2 vertices to create an edge
      const i2 = (i1 + 1) % polygon.length;
      const p1 = polygon[i1];
      const p2 = polygon[i2];

      // find the line perpendicular to this edge
      const normal = { x: p2.y - p1.y, y: p1.x - p2.x };

      minA = maxA = undefined;
      // for each vertex in the first shape, project it onto the line perpendicular to the edge
      // and keep track of the min and max of these values
      for (j = 0; j < polygonA.length; j++) {
        projected = normal.x * polygonA[j].x + normal.y * polygonA[j].y;
        if (isNil(minA) || projected < minA) {
          minA = projected;
        }
        if (isNil(maxA) || projected > maxA) {
          maxA = projected;
        }
      }

      // for each vertex in the second shape, project it onto the line perpendicular to the edge
      // and keep track of the min and max of these values
      minB = maxB = undefined;
      for (j = 0; j < polygonB.length; j++) {
        projected = normal.x * polygonB[j].x + normal.y * polygonB[j].y;
        if (isNil(minB) || projected < minB) {
          minB = projected;
        }
        if (isNil(maxB) || projected > maxB) {
          maxB = projected;
        }
      }

      // if there is no overlap between the projects, the edge we are looking at separates the two
      // polygons, and we know there is no overlap
      if (maxA < minB || maxB < minA) {
        return false;
      }
    }
  }

  return true;
}

const NODE_NAMES = {
  SPAN: 'SPAN',
  DIV: 'DIV',
};

const traverseNodeTreeForTexts = (node) => {
  const textList = [];
  if (!node.hasChildNodes()) return textList;
  const nodeArray = Array.from(node.childNodes);

  let currentTextGroup = [];
  nodeArray.forEach((node) => {
    const nodeType = node.nodeType;

    // If text node, append to current text group
    if (nodeType === Node.TEXT_NODE) {
      const nodeValue = node.nodeValue;

      // If nodeValue is not nil it is text (or an html comment)
      if (isNil(nodeValue)) return;
      currentTextGroup.push(nodeValue);
    }

    if (nodeType === Node.ELEMENT_NODE) {
      // If span, treat like inline text and append to current text group
      if (node.nodeName.toUpperCase() === NODE_NAMES.SPAN) {
        currentTextGroup.push(traverseNodeTreeForTexts(node).join('\n'));
      }

      if (node.nodeName.toUpperCase() === NODE_NAMES.DIV) {
        // Add previously grouped text to list first, then add div contents
        if (isNotEmpty(currentTextGroup)) {
          // Join everything in this group in to one text.
          textList.push(currentTextGroup.join(' '));
          currentTextGroup = [];
        }

        // Get the children of this div and add them to the list.
        const innerTextList = traverseNodeTreeForTexts(node);
        if (isEmpty(innerTextList)) {
          // Add an empty value for new line
          textList.push('');
        } else {
          // Add the inner texts values.
          textList.push(...innerTextList);
        }
      }
    }
  });

  if (isNotEmpty(currentTextGroup)) {
    // Join everything in this group in to one text.
    textList.push(currentTextGroup.join(' '));
  }
  return textList;
};

export const getContentEditableTextValue = (contentEditableEl) => {
  if (contentEditableEl.contentEditable !== 'true') {
    // This is here mostly as a self-imposed functionality limiter
    throw new Error('Unable to extract value from non contentEditable element');
  }

  const textArray = traverseNodeTreeForTexts(contentEditableEl);

  return textArray.join('\n');
};
